import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static values = {
    additionalHeight: Number,
    policyDuration: Number,
    withdrawalStartYear: Number,
  };

  initialize() {
    // https://github.com/hotwired/turbo-rails/issues/416#issuecomment-1715385853
    Chartkick.config.autoDestroy = false;

    if (!this.hasTurboLoadListener) {
      document.addEventListener(
        "turbo:render",
        this.handleTurboRender.bind(this)
      );
      this.hasTurboLoadListener = true;
    }
  }

  connect() {
    this.initializeCharts();
    this.debouncedLineChartDataAdder = this.debounce(() => {
      this.addLineChartData();
    });
    window.addEventListener("resize", this.debouncedLineChartDataAdder);
  }

  disconnect() {
    window.removeEventListener("resize", this.debouncedLineChartDataAdder);
  }

  handleTurboRender() {
    this.initializeCharts();
    Chartkick.eachChart((chart) => {
      chart.redraw();
    });
  }

  initializeCharts() {
    setTimeout(() => {
      this.addLineChartData();
    }, 500);
  }

  addLineChartData() {
    for (const chartName of Object.keys(Chartkick.charts)) {
      const chart = Chartkick.charts[chartName];

      if (!this._checkChartType(chartName, "chart-1")) {
        continue;
      }

      const datasets = chart.data;
      const chartValues = {};
      let maxValue = 0;
      datasets.forEach((el) => {
        const currData = el.data.slice(-1)[0][1];
        chartValues[el.name] = currData;
        if (currData > maxValue) maxValue = currData;
      });

      const lineChart = document.querySelector("#lineChart > div");

      datasets.forEach((dataset, index) => {
        const datasetMeta = chart.chart.getDatasetMeta(index);
        const lastPoint = datasetMeta.data[datasetMeta.data.length - 1];
        const { h: hue } = this.rgbaToHsl(lastPoint?.options.backgroundColor);

        if (hue == undefined) {
          return;
        }
        const existingDescription = this.element.querySelectorAll(
          `[data-name='${dataset.name}']`
        );
        const dataDesc = this._createDataDescription({
          value: chartValues[dataset.name],
          name: dataset.name,
          hue,
          maxValue,
        });
        const endPoint = this._createEndPoint({ hue, name: dataset.name });
        if (chartName === "chart-1") {
          if (existingDescription.length > 0)
            existingDescription.forEach((el) => el.remove());
          lineChart.insertAdjacentElement("beforeend", endPoint);
          lineChart.insertAdjacentElement("beforeend", dataDesc);
        }
      });

      const timeRangeDesc = this._createTimeRangeDescription({ datasets });
      lineChart.insertAdjacentElement("beforeend", timeRangeDesc);

      setTimeout(() => {
        const currChart = Chartkick.charts["chart-1"];
        if (!currChart) {
          return;
        }

        const currDatasets = currChart.data;
        const currBottoms = {};
        const currLefts = {};

        currDatasets.forEach((el, index) => {
          const metaData = currChart.chart.getDatasetMeta(index).data;
          const point = metaData[metaData.length - 1].getCenterPoint();
          const domY = point?.y;
          const domX = point.x - 4;

          currBottoms[el.name] = domY + this.additionalHeightValue;
          currLefts[el.name] = domX;
        });

        const dataDescs = document.querySelectorAll(`#data-desc`);
        dataDescs.forEach((el) => {
          const name = el.dataset.name;
          const endpoint = document.querySelector(
            `#end-point[data-name="${name}"]`
          );

          el.style.top = `${currBottoms[name] < 0 ? 0 : currBottoms[name] - 74}px`;
          el.style.left = `${currLefts[name] < 0 ? 0 : currLefts[name] + 20}px`;
          if (endpoint) {
            endpoint.classList.remove("hidden");
            endpoint.style.top = `${currBottoms[name] < 0 ? 0 : currBottoms[name] - 64}px`;
            endpoint.style.left = `${currLefts[name] < 0 ? 0 : currLefts[name]}px`;
          }

          // Remove unnecessary points
          Array.from(
            document.querySelectorAll(`#end-point[data-name="${name}"]`)
          )
            .filter((el) => !el.style.top)
            .forEach((el) => el.remove());

        });

        this._drawWithdrawalStartLine(currChart, lineChart);
      }, 500);
    }
  }

  _checkChartType(chartName, type) {
    return chartName.includes(type);
  }

  _createEndPoint({ name, hue, id = "end-point" }) {
    const endPoint = document.createElement("span");
    endPoint.className =
      "min-w-2 size-2 rounded-full transition-all absolute hidden";
    endPoint.style.backgroundColor = `hsl(${hue}, 58%, 55%)`;
    endPoint.id = id;
    endPoint.dataset.name = name;
    return endPoint;
  }

  _createDataDescription({ value, name, hue, id = "data-desc", maxValue = 0 }) {
    const dataDesc = document.createElement("span");
    const widthPercentage = maxValue === 0 ? 1 : value / maxValue;
    dataDesc.className = `px-2.5 py-1 text-black  w-fit font-medium text-[14px] leading-[20px] rounded-md transition-all ${value < maxValue ? "" : "absolute   -right-47"}`;
    dataDesc.innerText = `${new Intl.NumberFormat("de-DE", {
      style: "currency",
      currency: "EUR",
    }).format(value)}`;

    dataDesc.style.minWidth = `${widthPercentage * 193}px`;
    dataDesc.style.top = `0px`;
    dataDesc.style.backgroundColor = `hsl(${hue}, 53%, 79%, 0.71)`;

    if (value >= maxValue) {
      dataDesc.id = id;
      dataDesc.dataset.name = name;
      return dataDesc;
    }

    const wrapper = document.createElement("div");
    wrapper.className =
      "min-w-[193px] absolute transition-all -right-47 flex justify-start bg-blue-400 rounded-md overflow-hidden";
    wrapper.style.backgroundColor = `hsl(${hue}, 73%, 93%)`;
    wrapper.style.maxWidth = `193px`;
    wrapper.insertAdjacentElement("afterbegin", dataDesc);
    dataDesc.style.top = `0px`;
    // wrapper.style.top = `0px`;
    wrapper.dataset.name = name;
    wrapper.id = id;

    return wrapper;
  }

  // Marks the year the Entnahmephase (withdrawal phase) starts with a dashed vertical line. Finds
  // the matching data point's pixel position the same way the end-of-line labels above do (via
  // getDatasetMeta/getCenterPoint, not the scale API directly), since that's already proven to work
  // regardless of whether Chart.js treats the x-axis as linear or category here - Chartkick's
  // `library:` option is plain JSON and can't carry a custom Chart.js plugin/callback through to
  // draw this itself, so this has to happen after the fact, on the already-rendered chart.
  _drawWithdrawalStartLine(chart, lineChart) {
    const existingLine = document.querySelector("#withdrawal-start-line");
    if (existingLine) existingLine.remove();

    if (!this.hasWithdrawalStartYearValue || !this.withdrawalStartYearValue) {
      return;
    }

    const { chartArea } = chart.chart;
    const pointIndex = chart.data[0]?.data.findIndex(
      ([year]) => year === this.withdrawalStartYearValue
    );
    if (!chartArea || pointIndex == null || pointIndex < 0) {
      return;
    }

    const metaData = chart.chart.getDatasetMeta(0).data;
    const xPixel = metaData[pointIndex]?.getCenterPoint().x;
    if (xPixel == null) {
      return;
    }

    const line = document.createElement("div");
    line.id = "withdrawal-start-line";
    line.className =
      "absolute border-l-2 border-dashed border-gray-400 pointer-events-none";
    line.style.left = `${xPixel}px`;
    line.style.top = `${chartArea.top}px`;
    line.style.height = `${chartArea.bottom - chartArea.top}px`;
    lineChart.insertAdjacentElement("beforeend", line);
  }

  _createTimeRangeDescription({ datasets }) {
    const currValue = document.querySelector("#time-range-description");
    if (currValue) currValue.parentElement.removeChild(currValue);
    // Prefer the actual policy_duration input over counting chart data points: a non-January
    // start adds one extra (partial) calendar-year row to the chart data for calculation
    // accuracy, which would otherwise make this count one year too many.
    const timeRange =
      this.policyDurationValue ||
      datasets[0].data[datasets[0].data.length - 1][0] -
        datasets[0].data[0][0] +
        1;
    const timeRangeDesc = document.createElement("span");
    timeRangeDesc.className =
      "absolute -bottom-4 right-5 text-[#9CA3AF] text-[12px] leading-[20px]";
    timeRangeDesc.innerText = `Laufzeit ${timeRange} Jahre`;
    timeRangeDesc.id = "time-range-description";
    return timeRangeDesc;
  }

  rgbaToHsl(rgba) {
    if (!rgba || typeof rgba !== "string") return {};

    const rgbaRegex = /rgba?\((\d+),\s*(\d+),\s*(\d+),\s*([\d.]+)\)/;
    const match = rgba.match(rgbaRegex);
    if (!match) return {};

    const [, r, g, b, a] = match.map(parseFloat);

    const min = Math.min(r, g, b);
    const max = Math.max(r, g, b);

    let l = (min + max) / 2;

    let s = 0;
    if (min !== max) {
      s = l > 0.5 ? (max - min) / (2 - max - min) : (max - min) / (max + min);
    }

    let h = 0;
    if (min !== max) {
      if (max === r) {
        h = (g - b) / (max - min);
      } else if (max === g) {
        h = 2 + (b - r) / (max - min);
      } else {
        h = 4 + (r - g) / (max - min);
      }
    }
    h *= 60;
    if (h < 0) {
      h += 360;
    }

    h = Math.round(h * 100) / 100;
    s = Math.round(s * 100) / 100;
    l = Math.round(l * 100) / 100;

    return { h, s, l };
  }

  debounce(func, timeout = 300) {
    let timer;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        func.apply(this, args);
      }, timeout);
    };
  }
}
